import styled from '@emotion/styled'
import { Box, BoxProps, ButtonProps, Paper } from '@mui/material';
import BackgroundBorderTopShort from '../../assets/borders/chat-border-top.svg';
import BackgroundBorderBottomShort from '../../assets/borders/chat-border-bottom.svg'
import BackgroundBorderTopLarge from '../../assets/borders/border-top-large.svg';
import BackgroundBorderBottomLarge from '../../assets/borders/border-bottom-large.svg'
import BackgroundBorderTopExtraLarge from '../../assets/borders/border-top-extra-large.svg';
import React, { ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { convertToPixelFromVW } from '../../utils/ViewportUtils';

const getBackground = (bordertype?: "none" | "short" | "large" | "xlarge") => {
    if (bordertype === 'none') return {};
    if (bordertype === "xlarge")
        return {
            background: `url('${BackgroundBorderTopExtraLarge}') no-repeat, url('${BackgroundBorderBottomLarge}') no-repeat, transparent`,
            backgroundPosition: 'top left, bottom right',
            backgroundSize: '35%, 10%'
        };

    else if (bordertype === "large")
        return {
            background: `url('${BackgroundBorderTopLarge}') no-repeat, url('${BackgroundBorderBottomLarge}') no-repeat, transparent`,
            backgroundPosition: 'top left, bottom right',
            backgroundSize: '15%, 12.5%'
        }

    return {
        background: `url('${BackgroundBorderTopShort}') no-repeat, url('${BackgroundBorderBottomShort}') no-repeat, transparent`,
        backgroundPosition: 'top left, bottom right',
        backgroundSize: '35%, 10%'
    }
}

const EdgeSizes = {
    none: {
        edge: convertToPixelFromVW(2),
        borderRadius: 3,
        padding: 0.5,
    },
    short: {
        edge: convertToPixelFromVW(1.125),
        borderRadius: 3,
        padding: 0.25,
    },
    large: {
        edge: convertToPixelFromVW(2),
        borderRadius: 3,
        padding: 0.5,
    },
    xlarge: {
        edge: convertToPixelFromVW(2),
        borderRadius: 3,
        padding: 0.5,
    },
}

const CustomBorderedBox = styled(Box)<{ bordertype?: "none" | "short" | "large" | "xlarge", padding: number } & ButtonProps>((
    { bordertype: borderType = "short", padding }) => ({
        ...getBackground(borderType),
        width: '100%',
        // padding: `${padding * 1.6}px ${padding}px ${padding * 1.5}px ${padding}px`,
        padding: `${padding}vw`,
        paddingTop: `${padding + 0.25}vw`,
        boxSizing: 'border-box',
        borderRadius: 0,
    }))

interface BoxWrapperProps extends BoxProps {
    children: ReactNode;
    borderSize?: "none" | "short" | "large" | "xlarge";
    bgcolor?: string;
}

const CustomShaped: React.FC<BoxWrapperProps> = ({
    children,
    borderSize = "short",
    bgcolor = 'var(--text-light)',
}) => {

    const { edge, borderRadius, padding } = EdgeSizes[borderSize];

    const ref = useRef(null);
    const [dimesions, setDimensions] = useState({
        width: window.innerWidth,
        height: window.innerHeight,
    });

    useEffect(() => {
        const handleResize = () => {
            if (ref.current) {
                const { clientWidth, clientHeight } = ref.current;
                setDimensions({
                    width: clientWidth,
                    height: clientHeight,
                });
            }
        };

        const resizeObserver = new ResizeObserver(handleResize);
        const currentRef = ref.current;
        if (currentRef) {
            resizeObserver.observe(currentRef);
            handleResize();
        }

        window.addEventListener('resize', handleResize);
        return () => {
            window.removeEventListener('resize', handleResize);
            if (currentRef)
                resizeObserver.unobserve(currentRef);
            resizeObserver.disconnect();
        };
    }, []);

    const adjustEdge = useMemo(() => {
        if(borderSize === 'large') {
            return convertToPixelFromVW(0.3);
        }
        return 0;
    }, [borderSize])

    const bottomEdge = useMemo(() => {
        if(borderSize === 'xlarge'){
            return edge * .75
        }
        return edge;
    }, [borderSize, edge])

    const getClipPathForFilters = useMemo(() => {
        return `M ${edge + adjustEdge} 0 H ${dimesions.width} V ${dimesions.height - bottomEdge + adjustEdge} L ${dimesions.width - bottomEdge} ${dimesions.height} H 0 V ${edge} Z`
    }, [adjustEdge, bottomEdge, dimesions.height, dimesions.width, edge])

    return <CustomBorderedBox bordertype={borderSize} padding={padding}>
        <Paper ref={ref}
            elevation={0}
            sx={{
                background: bgcolor,
                width: { xs: '100%' },
                height: '100%',
                boxSizing: 'border-box',
                padding: 3,
                borderRadius: borderRadius,
                clipPath: `path('${getClipPathForFilters}')`,
                textAlign: 'left'
            }}>
            {children}
        </Paper>
    </CustomBorderedBox>
}

export default CustomShaped;